import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Component, type ErrorInfo, lazy, type ReactNode, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ApiError } from './api/client';
import { AppBar } from './components/AppBar';
import { BottomNav } from './components/BottomNav';
import { EmptyState } from './components/EmptyState';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { AnalyticsScreen } from './screens/AnalyticsScreen';
import { DetailScreen } from './screens/DetailScreen';
import { FeedScreen } from './screens/FeedScreen';
import { LogsScreen } from './screens/LogsScreen';
import { MapScreen } from './screens/MapScreen';

// Phase 3.5 — SettingsScreen is the first lazy-loaded screen. The
// chunk is fetched only when the user navigates to ``/settings``,
// keeping the FeedScreen / DetailScreen JSX path off the critical
// load. Phase 3.4 (Map + Analytics) will extend this pattern with
// their heavier leaflet/recharts dependencies.
const SettingsScreen = lazy(() => import('./screens/SettingsScreen'));

// React Query — sensible defaults. We override per-hook (useStatus,
// useJobs) where polling cadences matter.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on auth/permission failures — the user can't fix it
        // by waiting.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/** App-wide ErrorBoundary. Catches render-time exceptions (e.g. a
 *  component throws because of a typo); React Query errors are
 *  rendered inline by each screen instead.
 */
class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console; future Phase 3.x may ship logs to /api/v1/errors.
    console.error('RootErrorBoundary caught', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="screen-stub">
          <div className="crosshair-big" />
          <h2>Ошибка приложения</h2>
          <p>{this.state.error.message || 'неизвестная ошибка'}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function Shell() {
  // Activates theme sync — has no UI of its own.
  useTelegramWebApp();

  return (
    <div className="app">
      <AppBar />
      <Routes>
        <Route path="/" element={<FeedScreen />} />
        <Route path="/job/:jobId" element={<DetailScreen />} />
        <Route path="/map" element={<MapScreen />} />
        <Route path="/analytics" element={<AnalyticsScreen />} />
        <Route path="/logs" element={<LogsScreen />} />
        <Route
          path="/settings"
          element={
            <Suspense fallback={<EmptyState>загрузка настроек…</EmptyState>}>
              <SettingsScreen />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
    </div>
  );
}

export function App() {
  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}
